import { useMemo, type ReactElement } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DataTableAlertsContent } from './DataTableAlertsTab';
import {
  parseOccurrencesAlertKey,
  parseAlertToken,
} from '@kirin/api-client/helpers/alerts';
import {
  AlertOccurrencesModal,
  ScopedAlertsView,
} from '@/components/grouped-alerts';
import { getOccurrencesFiltersFromSearchParams } from './DataTableAlertsTab/utils';

export default function OrgGroupAlertsTab(): ReactElement | null {
  const params = useParams<{ organizationId: string; '*': string }>();
  const { organizationId } = params;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const splat = params['*'];
  const occurrencesAlertKey = parseOccurrencesAlertKey(splat);
  // KIR-2788: a notification click lands on the exact alert(s) that fired — one
  // opaque token (a finding_key or an alert-set id), resolved by the backend.
  const alertToken = parseAlertToken(splat);

  const occurrencesFilters = useMemo(
    () => getOccurrencesFiltersFromSearchParams(searchParams),
    [searchParams]
  );

  if (!organizationId) {
    return null;
  }

  // Unified notification landing — the exact alert(s) the token resolves to,
  // shown in the real alerts table scoped to that set (KIR-2788, Option B).
  if (alertToken) {
    return (
      <ScopedAlertsView
        scope={{ kind: 'org', organizationId }}
        token={alertToken}
      />
    );
  }

  return (
    <>
      <DataTableAlertsContent
        key={organizationId}
        organizationId={organizationId}
      />
      {occurrencesAlertKey && (
        <AlertOccurrencesModal
          open
          alertKey={occurrencesAlertKey}
          organizationId={organizationId}
          filters={occurrencesFilters}
          onClose={() => navigate(`/${organizationId}/alerts`)}
        />
      )}
    </>
  );
}
